import React, { useCallback, useState } from 'react';

import type { BackgroundProps } from '../types/component';
import type { ModeEnum } from '../types/ModeEnum';

import { ImageLoaded, RGBColor } from '../../../../editor';
import Inner from './Inner';

export type BackgroundState = {
  backgroundColorPreview?: RGBColor;
  gradientDegPreview?: number;
  gradientDegPreviewIndex?: number;
  gradientOpacityPreview?: number;
  gradientOpacityPreviewIndex?: number;
  gradientColorPreview?: RGBColor;
  gradientColorPreviewIndex?: number;
  gradientColorPreviewColorIndex?: number;
  darkenPreview?: number;
  lightenPreview?: number;
  imagePreview?: ImageLoaded;
};

const BackgroundDefaultControls: React.FC<BackgroundProps> = (props) => {
  const [state, setState] = useState<BackgroundState>({});

  const handleChangeDarken = useCallback(() => {
    props.onChange({ darken: state.darkenPreview });
    setState((prevState) => ({ ...prevState, darkenPreview: undefined }));
  }, [props, state.darkenPreview]);

  const handleChangeDarkenPreview = useCallback((value: number) => {
    setState((prevState) => ({ ...prevState, darkenPreview: value }));
  }, []);

  const handleChangeLighten = useCallback(() => {
    props.onChange({ lighten: state.lightenPreview });
    setState((prevState) => ({ ...prevState, lightenPreview: undefined }));
  }, [props, state.lightenPreview]);

  const handleChangeLightenPreview = useCallback((value: number) => {
    setState((prevState) => ({ ...prevState, lightenPreview: value }));
  }, []);

  const handleChangeHasPadding = useCallback(() => {
    props.onChange({
      hasPadding:
        props.data.hasPadding === undefined
          ? !props.defaultHasPadding
          : !props.data.hasPadding,
    });
  }, [props]);

  const handleChangeBackgroundColorPreview = useCallback((e?: RGBColor) => {
    setState((prevState) => ({ ...prevState, backgroundColorPreview: e }));
  }, []);

  const handleChangeGradientDegPreview = useCallback(
    (
      gradientDegPreview: number | undefined,
      gradientDegPreviewIndex?: number
    ) => {
      setState((prevState) => ({
        ...prevState,
        gradientDegPreview,
        gradientDegPreviewIndex,
      }));
    },
    []
  );

  const handleChangeGradientOpacityPreview = useCallback(
    (
      gradientOpacityPreview: number | undefined,
      gradientOpacityPreviewIndex?: number
    ) => {
      setState((prevState) => ({
        ...prevState,
        gradientOpacityPreview,
        gradientOpacityPreviewIndex,
      }));
    },
    []
  );

  const handleChangeGradientColorPreview = useCallback(
    (
      gradientColorPreview: RGBColor | undefined,
      gradientColorPreviewIndex?: number,
      gradientColorPreviewColorIndex?: number
    ) => {
      setState((prevState) => ({
        ...prevState,
        gradientColorPreview,
        gradientColorPreviewIndex,
        gradientColorPreviewColorIndex,
      }));
    },
    []
  );

  const handleImageLoaded = useCallback((imagePreview: ImageLoaded) => {
    setState((prevState) => ({ ...prevState, imagePreview }));
  }, []);

  const handleImageUploaded = useCallback(() => {
    setState((prevState) => ({ ...prevState, imagePreview: undefined }));
  }, []);

  const handleChangeModeSwitch = useCallback(
    (mode: ModeEnum | undefined, modeFlag: ModeEnum | undefined) => () => {
      if (mode != undefined && modeFlag != undefined) {
        modeFlag ^= mode;
        props.onChange({ modeFlag });
      } else {
        props.onChange({ modeFlag });
      }
    },
    [props]
  );

  return (
    <Inner
      {...props}
      handleChangeDarken={handleChangeDarken}
      handleChangeDarkenPreview={handleChangeDarkenPreview}
      handleChangeLighten={handleChangeLighten}
      handleChangeLightenPreview={handleChangeLightenPreview}
      handleChangeHasPadding={handleChangeHasPadding}
      handleChangeModeSwitch={handleChangeModeSwitch}
      handleChangeBackgroundColorPreview={handleChangeBackgroundColorPreview}
      handleChangeGradientDegPreview={handleChangeGradientDegPreview}
      handleChangeGradientOpacityPreview={handleChangeGradientOpacityPreview}
      handleChangeGradientColorPreview={handleChangeGradientColorPreview}
      handleImageLoaded={handleImageLoaded}
      handleImageUploaded={handleImageUploaded}
      {...state}
    />
  );
};

export default React.memo(BackgroundDefaultControls);
